import { useCallback, useState } from 'react';
import { IMessageInfo, IMultiSuggestResult } from '@cian/geosuggest-widget';
import { debounce } from 'throttle-debounce';
import { useDispatch } from 'react-redux';
import { getGeoSuggest } from '../../../actions/geoSuggest';
import { TGeoType } from '../types';
import { isEmpty } from 'ramda';
import { ILocation } from '../../../types/location';

const MIN_SEARCH_LENGTH = 3;

export const NOT_FOUND_ERROR: IMessageInfo = {
  title: 'Ничего не найдено',
  text: 'Измените параметры поиска',
};

export interface IUseGeoTagsSuggestProps {
  location: ILocation;
  types: TGeoType[];
}

export const useGeoTagsSuggest = ({ location, types }: IUseGeoTagsSuggestProps) => {
  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState(false);
  const [suggestError, setSuggestError] = useState<IMessageInfo>();
  const [suggestData, setSuggestData] = useState<IMultiSuggestResult>();

  const getSuggestions = useCallback(
    async (value: string) => {
      if (value.length < MIN_SEARCH_LENGTH) {
        return;
      }

      setIsFetching(true);

      const suggestResult = await dispatch(
        getGeoSuggest({
          types,
          location,
          request: value,
        }) as unknown as Promise<IMultiSuggestResult>,
      );

      setSuggestError(undefined);
      setIsFetching(false);

      if (!isEmpty(suggestResult.suggestions)) {
        return setSuggestData(suggestResult);
      }

      setSuggestError(NOT_FOUND_ERROR);
      setSuggestData(undefined);
    },
    [location, types, dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getGeoSuggestDebounced = useCallback(debounce(200, getSuggestions), [getSuggestions]);

  const clearSuggestionData = useCallback(() => setSuggestData(undefined), [setSuggestData]);

  return {
    isFetching,
    suggestError,
    suggestData,
    clearSuggestionData,
    getSuggestions: getGeoSuggestDebounced,
  };
};
